import { useMemo, useState } from 'react';
import { App } from 'antd';
import { useTranslation } from 'react-i18next';
import { usePermission } from '~/app/auth/hooks/usePermission';
import { PermissionKey } from '@evimo/types/enums/permission-key';
import { useWorkOrderMergeSuggestions } from '~/app/hooks/maintenance-execution/useWorkOrderMergeSuggestions';
import { useWorkOrderMutations } from '~/app/hooks/maintenance-execution/useWorkOrderMutations';
import type {
  OptimizerClusterView,
  OptimizerWorkOrderLabel,
  PendingApplyMerge,
} from '../types';

export interface UseOptimizerMergeSuggestionsArgs {
  organizationId: string;
  siteFilter: string;
  pageSize: number;
  workOrderById: Map<string, OptimizerWorkOrderLabel>;
  siteNameById: Map<string, string>;
  /** When true, keep page at 1 (Kanban preview). */
  lockToFirstPage?: boolean;
}

export function useOptimizerMergeSuggestions({
  organizationId,
  siteFilter,
  pageSize,
  workOrderById,
  siteNameById,
  lockToFirstPage = false,
}: UseOptimizerMergeSuggestionsArgs) {
  const { t } = useTranslation('maintenance');
  const { message } = App.useApp();
  const { hasPermission } = usePermission();
  const canManage = hasPermission(PermissionKey.WORK_ORDER_MANAGE);

  const [page, setPage] = useState(1);
  const [pendingApply, setPendingApply] = useState<PendingApplyMerge | null>(
    null,
  );

  const effectivePage = lockToFirstPage ? 1 : page;

  const query = useWorkOrderMergeSuggestions(organizationId, {
    page: effectivePage,
    limit: pageSize,
    ...(siteFilter ? { siteId: siteFilter } : {}),
  });
  const { applyMerge } = useWorkOrderMutations(organizationId);

  const clusters = useMemo((): OptimizerClusterView[] => {
    const raw = query.data?.clusters ?? [];
    return raw.map((cluster) => ({
      ...cluster,
      siteName:
        siteNameById.get(cluster.siteId) ?? t('workOrders.kanban.unknownSite'),
      workOrders: cluster.workOrderIds.map((id) => {
        const wo = workOrderById.get(id);
        return {
          id,
          code: wo?.code ?? id.slice(0, 8),
          status: wo?.status ?? 'PLANNED',
        };
      }),
    }));
  }, [query.data?.clusters, siteNameById, t, workOrderById]);

  const total = query.data?.meta?.total ?? 0;

  const openApply = (cluster: OptimizerClusterView) => {
    const defaultTarget = cluster.workOrders[0]?.id;
    if (!defaultTarget) return;
    setPendingApply({ cluster, targetWorkOrderId: defaultTarget });
  };

  const confirmApply = async () => {
    if (!pendingApply) return;
    const { cluster, targetWorkOrderId } = pendingApply;
    const sourceWorkOrderIds = cluster.workOrderIds.filter(
      (id) => id !== targetWorkOrderId,
    );
    try {
      await applyMerge.mutateAsync({
        targetWorkOrderId,
        sourceWorkOrderIds,
      });
      message.success(t('workOrders.kanban.optimizer.applySuccess'));
      setPendingApply(null);
    } catch {
      message.error(t('workOrders.kanban.optimizer.applyFailed'));
    }
  };

  return {
    canManage,
    clusters,
    isLoading: query.isLoading,
    isError: query.isError,
    meta: query.data?.meta,
    total,
    page: effectivePage,
    setPage,
    pendingApply,
    setPendingApply,
    openApply,
    confirmApply,
    isApplying: applyMerge.isPending,
  };
}
